import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MoonStar } from "lucide-react";
import { resolveImg } from "@/lib/content";
import { api } from "@/lib/api";

const ease = [0.16, 1, 0.3, 1];

const CHRISTMAS_EVE = new Date("2026-12-24T00:00:00");
const AFTER_CHRISTMAS = new Date("2026-12-26T00:00:00");

const pad = (n) => String(n).padStart(2, "0");

const Countdown = () => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const ms = CHRISTMAS_EVE - now;

  if (now >= AFTER_CHRISTMAS) {
    return (
      <div data-testid="hero-countdown" className="mt-10 text-sm text-[#FBF7F0]/70">
        Happy Christmas — see you next year.
      </div>
    );
  }
  if (ms <= 0) {
    return (
      <div
        data-testid="hero-countdown"
        className="inline-flex items-center gap-3 rounded-2xl border border-[#FBF7F0]/15 bg-white/5 backdrop-blur-md px-6 py-4 w-fit text-[#FBF7F0]"
      >
        <MoonStar className="w-5 h-5 text-[#FBF7F0]/70" strokeWidth={1.6} />
        <span className="font-serif text-2xl">It's happening — happy Christmas!</span>
      </div>
    );
  }

  const sleeps = Math.ceil(ms / 86400000);
  const hours = Math.floor(ms / 3600000) % 24;
  const mins = Math.floor(ms / 60000) % 60;
  const secs = Math.floor(ms / 1000) % 60;

  return (
    <div
      data-testid="hero-countdown"
      className="inline-flex items-center gap-5 rounded-2xl border border-[#FBF7F0]/15 bg-white/5 backdrop-blur-md px-6 py-4 w-fit"
    >
      <span className="font-serif font-light text-5xl md:text-6xl leading-none text-[#FBF7F0]" data-testid="hero-countdown-days">
        {sleeps}
      </span>
      <span>
        <span className="block text-xs uppercase tracking-[0.25em] font-semibold text-[#FBF7F0]/85">
          {sleeps === 1 ? "sleep" : "sleeps"} until Christmas Eve
        </span>
        <span className="mt-1 block text-base text-[#FBF7F0]/90 tabular-nums" data-testid="hero-countdown-clock">
          {pad(hours)}:{pad(mins)}:{pad(secs)}
        </span>
      </span>
    </div>
  );
};

const PlacesLeft = () => {
  const [days, setDays] = useState(null);

  useEffect(() => {
    api
      .get("/shifts")
      .then(({ data }) => setDays(data.days || []))
      .catch(() => {});
  }, []);

  if (!days || !days.length) return null;

  return (
    <>
      {days.map((d) => {
        const left = (d.roles || []).reduce(
          (a, r) => a + (r.slots || []).reduce((x, s) => x + s.remaining, 0),
          0
        );
        const slug = d.label.toLowerCase().replace(/\s+/g, "-");
        return (
          <div
            key={d.id}
            data-testid={`hero-places-${slug}`}
            className="inline-flex items-center gap-4 rounded-2xl border border-[#FBF7F0]/15 bg-white/5 backdrop-blur-md px-6 py-4"
          >
            <span
              className="font-serif font-light text-5xl md:text-6xl leading-none text-[#FBF7F0]"
              data-testid={`hero-places-count-${slug}`}
            >
              {left}
            </span>
            <span>
              <span className="block text-xs uppercase tracking-[0.25em] font-semibold text-[#FBF7F0]/85">
                {left === 1 ? "place" : "places"} left
              </span>
              <span className="mt-1 block text-base font-medium text-[#FBF7F0]/90">{d.label}</span>
            </span>
          </div>
        );
      })}
    </>
  );
};

const Line = ({ children, delay = 0 }) => (
  <span className="reveal-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onSignUp, c }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full bg-[#8E1226] text-[#FBF7F0] overflow-hidden"
    >
      {/* Parallax image on the right */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute top-0 right-0 h-full w-full md:w-[46%] z-0"
      >
        <div className="relative h-full w-full">
          {resolveImg(c.hero_image) ? (
            <img
              src={resolveImg(c.hero_image)}
              alt="A volunteer handing over a warm plate of food"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-[#6B0E1A]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-[#8E1226] via-[#8E1226]/40 to-transparent md:from-[#8E1226] md:via-[#8E1226]/20" />
          <div className="absolute inset-0 bg-[#8E1226]/20" />
        </div>
      </motion.div>

      <motion.div
        style={{ y: overlayY }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 min-h-screen flex flex-col justify-center pt-24 pb-16"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-[#FBF7F0]/80" />
          <span className="text-[11px] md:text-xs uppercase tracking-[0.32em] font-semibold text-[#FBF7F0]/90">
            {c.hero_eyebrow}
          </span>
        </motion.div>

        <h1 className="font-serif font-light leading-[0.92] tracking-tight text-[15vw] sm:text-[12vw] md:text-8xl lg:text-[8.5rem] max-w-5xl">
          <Line delay={0.35}>{c.hero_line_1}</Line>
          <Line delay={0.47}>
            <span className="italic">{c.hero_line_2}</span>.
          </Line>
          <Line delay={0.59}>{c.hero_line_3}</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease }}
          className="mt-8 max-w-xl text-lg md:text-xl text-[#FBF7F0]/90 leading-relaxed"
        >
          {c.hero_paragraph}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-signup-btn"
            onClick={onSignUp}
            className="group w-full sm:w-fit inline-flex items-center justify-center rounded-full bg-[#FBF7F0] text-[#8E1226] px-6 sm:px-12 py-5 sm:py-6 text-base sm:text-xl font-bold tracking-wide transition-transform duration-300 hover:scale-[1.04] hover:bg-white"
          >
            {c.hero_cta}
            <ArrowDown className="inline-block ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-y-1" strokeWidth={2} />
          </button>
          <span className="text-base text-[#FBF7F0]/80">
            {c.hero_note}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease }}
          className="mt-10 flex flex-wrap gap-4 items-stretch"
        >
          <Countdown />
          <PlacesLeft />
        </motion.div>
      </motion.div>
    </section>
  );
};
